/**
 * Full-screen loader shown until bootstrap API data and i18n are ready.
 */
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppBootstrap } from '@/hooks/useAppBootstrap'
import logoImg from '@/assets/logo.png'

function AppBootstrapLoader() {
  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 via-white to-primary-50/30"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading store"
    >
      <div className="flex flex-col items-center gap-8 px-6 animate-fade-in-up">
        <img
          src={logoImg}
          alt="Arts and Handicrafts"
          className="h-24 w-auto object-contain drop-shadow-sm sm:h-28"
        />

        <div className="flex flex-col items-center gap-3">
          <div className="relative h-10 w-10">
            <div className="absolute inset-0 rounded-full border-2 border-primary-100" />
            <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-primary-600 border-r-primary-500" />
          </div>
          <p className="text-sm font-medium text-gray-600">Loading your store…</p>
        </div>
      </div>
    </div>
  )
}

interface AppBootstrapGateProps {
  children: ReactNode
}

export function AppBootstrapGate({ children }: AppBootstrapGateProps) {
  const { pathname } = useLocation()
  const { isReady } = useAppBootstrap()

  // POS routes should open immediately — no need to wait for storefront bootstrap.
  if (pathname.startsWith('/pos')) {
    return <>{children}</>
  }

  if (!isReady) {
    return <AppBootstrapLoader />
  }

  return <>{children}</>
}
